import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { MoreHorizontal, Check, CheckCircle2 } from "lucide-react";
import { OnboardingStage } from "@/pages/settings/components/contractsSettings/types";
import { Button } from "@/components/ui/button";
import { IconButton } from "@/components/ui/icon-button";
import Text from "@/components/ui/text";
import IconContainer from "@/components/ui/icon-container";
import { Redo } from "@dreamhub/designsystem/icons";
import {
  Popover,
  PopoverContent,
  PopoverTrigger
} from "@/components/ui/popover";
import {
  Command,
  CommandGroup,
  CommandItem,
  CommandList,
  CommandSeparator
} from "@/components/ui/command";
import { cn } from "@/utils/utils";

interface Props {
  stages: OnboardingStage[];
  currentStageId: string | null;
  onProceedStage: (stageId: string) => void;
  onComplete: () => void;
}

const OnboardingStagesPanelFooter: React.FC<Props> = ({
  stages,
  currentStageId,
  onProceedStage,
  onComplete
}) => {
  const { t } = useTranslation();
  const [isPickerOpen, setIsPickerOpen] = useState(false);

  const currentIndex = stages.findIndex((stage) => stage.id === currentStageId);
  const currentStageNumber = currentIndex >= 0 ? currentIndex + 1 : 1;
  const nextStage = currentIndex >= 0 ? stages[currentIndex + 1] ?? null : null;

  const handleProceed = () => {
    if (nextStage) onProceedStage(nextStage.id);
    else onComplete();
  };

  return (
    <div className="bg-surface-elevation1 border border-border-border1 rounded-2xl p-3 flex flex-col gap-2">
      <Text variant="smBodyTertiary">
        {t("contracts.detailedView.onboarding.stageOf", {
          current: currentStageNumber,
          total: stages.length
        })}
      </Text>
      <div className="flex gap-2">
        <Button
          variant="secondary"
          className="flex-1 h-9"
          onClick={handleProceed}
          data-test="onboarding-panel-proceed-button"
        >
          <IconContainer size="5">
            <Redo />
          </IconContainer>
          <Text variant="label">
            {nextStage
              ? `${t("contracts.detailedView.onboarding.proceedTo")} ${nextStage.name}`
              : t("contracts.detailedView.onboarding.completeOnboarding")}
          </Text>
        </Button>
        <Popover open={isPickerOpen} onOpenChange={setIsPickerOpen}>
          <PopoverTrigger asChild>
            <IconButton
              variant="icon"
              size="md"
              className="h-9 w-9 flex-shrink-0"
              data-test="onboarding-panel-stage-picker-button"
            >
              <MoreHorizontal />
            </IconButton>
          </PopoverTrigger>
          <PopoverContent
            padding={4}
            className="flex flex-col gap-2 w-fit"
            align="end"
            onEscapeKeyDown={() => setIsPickerOpen(false)}
            sideOffset={4}
          >
            <Command>
              <CommandList>
                <CommandGroup>
                  <Text variant="smBodyTertiary" className="px-2 py-1 block">
                    {t(
                      "contracts.detailedView.onboarding.onboardingStagePickerLabel"
                    )}
                  </Text>
                  {stages.map((stage) => {
                    const isCurrent = stage.id === currentStageId;
                    return (
                      <CommandItem
                        key={stage.id}
                        onSelect={() => {
                          if (isCurrent) return;
                          setIsPickerOpen(false);
                          onProceedStage(stage.id);
                        }}
                        className={cn(
                          "w-full cursor-pointer py-2 pl-2 pr-3 min-h-9 flex justify-between rounded-lg transition-all duration-200 ease-in-out",
                          isCurrent
                            ? "font-semibold cursor-not-allowed !bg-transparent hover:!bg-transparent"
                            : "hover:!bg-surface-hover-soft active:!bg-surface-pressed"
                        )}
                      >
                        <Text variant={isCurrent ? "label" : "body"}>
                          {stage.name}
                        </Text>
                        {isCurrent && (
                          <IconContainer size="5">
                            <Check />
                          </IconContainer>
                        )}
                      </CommandItem>
                    );
                  })}
                  <CommandSeparator />
                  <CommandItem
                    onSelect={() => {
                      setIsPickerOpen(false);
                      onComplete();
                    }}
                    className="w-full cursor-pointer py-2 pl-2 pr-3 min-h-9 flex items-center gap-2 rounded-lg hover:!bg-surface-hover-soft active:!bg-surface-pressed"
                  >
                    <IconContainer size="5" color="secondary">
                      <CheckCircle2 />
                    </IconContainer>
                    <Text variant="body">
                      {t("contracts.detailedView.onboarding.completeOnboarding")}
                    </Text>
                  </CommandItem>
                </CommandGroup>
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>
      </div>
    </div>
  );
};

export default OnboardingStagesPanelFooter;
